import {QA, SectionDivider, isSectionDivider} from "@components/Faq/Faq";
import Image from "next/image";
import Link from "next/link";
import {Trans, useTranslation} from "ni18n";
import React, {useMemo} from "react";
import {supportLink, v5Pages} from "src/components/_common/_constants";

import {minMaxTravelClearancePrices} from "../constants/CovidTravelClearanceConstants";
import useDeepCompareMemo from "./useDeepCompareMemo";

const questionList = [
  "How does partnering with Carbon Health healthcare lower costs?",
  "How do you ensure student satisfaction?",
  "Is Carbon Health HIPAA compliant?",
  "Is the Carbon Health app secure?",
  "What protections does Carbon Health have in place to protect personal and health information from hackers or outsiders getting into the app or into their databases that store my information?",
  "Is Carbon Health configured to meet the travel or entry requirements to any destination?",
  "How do I book COVID-19 testing with my family?",
  "How do I add a child or family member who is a minor to my Carbon Health account?",
  "If my partner or family is traveling with me, is it possible to get tested at the same time?",
  "How much does COVID-19 travel testing cost?",
  "Can I pay with my HSA/FSA Card?",
  "What if my test result is invalid?",
  "How does the COVID-19 Test work?",
  "Where do I find my COVID-19 Test Results?",
  "How do I download my COVID-19 Test Results PDF?",
  "There are no appointments available at my desired testing location. Can I walk in?",
  "Is the test a nucleic acid amplification test (NAAT) test?",
  "How do I find the Portland Airport COVID-19 testing location?",
  "Do I need to have my flight itinerary for a testing appointment?",
  "What is the Health Pass?",
  "Who can use the Health Pass?",
  "What type of data does the Health Pass contain?",
  "Where do I find my Health Pass?",
  "How do I find my Health Pass if I did not provide an email address during sign up, or if I didn’t receive a text message after my vaccination?",
  "I received my vaccine at a County of Los Angeles location before Carbon Health took over the site (typically before February 1st, 2021). Can I add my vaccine information to my Health Pass?",
  "Does the Health Pass integrate with other applications?",
  "Can you add my vaccination record to my Health Pass if I did not get vaccinated at a Carbon Health site?",
  "My Health Pass information is missing or wrong. What do I do?",
  "How much does COVID-19 testing cost?",
  "How much does COVID-19 testing cost? ", // added a space to make it unique because we want to show different answers to this question (see the question above)
  "How much does COVID-19 testing, treatment, and vaccines cost?",
  "Do you accept my insurance?",
  "How much will my co-pay be?",
  "What are co-pays?",
  "What are deductibles?",
  "What does it mean if my insurance is not verified?",
  "How do I add my insurance if I'm on my family's plan?",
  "When will I get my bill?",
  "My credit card was charged by 'direct urgent care'",
  "How to book an in-person visit",
  "Can I select a new primary care provider?",
  "Why is the location now being run by Carbon Health?",
  "Will my insurance be out of network now?",
  "How do I book an appointment?",
  "Are there any membership fees?",
  "Who are the people I will be speaking to through the virtual service?",
  "Who is eligible for serological antibody testing?",
  "How can I get an antibody test?",
  "How long does it take to get serological antibody results back?",
  "What does a negative antibody result indicate?",
  "Why is Carbon Health offering the antibody test?",
  "What does it mean for me if I test positive with the antibody test?",
  "Do I need to retake the antibody test every few weeks to continue to confirm immunity?",
  "What is the false-negative rate for the antibody test?",
  "How much does a COVID-19 Clearance Exam cost?",
  "When do I need to schedule a COVID-19 Clearance Exam?",
  "When can I expect the results of my COVID-19 Clearance Exam?",
  "I'm not currently in the location where my procedure will be taking place. Can I get a COVID-19 Clearance Exam elsewhere?",
  "How much does it cost to enroll in COVID Positive Care?",
  "Why is Carbon Health offering this program?",
  "What happens if a patient develops COVID-related complications while in the program?",
  "Will Carbon Health be providing repeat testing for patients enrolled in the program?",
  "How are mental health referrals handled?",
  "How does COVID Ready work?",
  "How do employees onboard through the Carbon Health app?",
  "Which test is used to test employees for COVID-19?",
  "Can employers see an employee's private medical information?",
  "What employee information is being tracked?",
  "Is employee data protected?",
  "Can employees control what data is shared?",
  "How does Carbon perform Employee Contact Tracing?",
  "How does Carbon do Workplace Contact Tracing?",
  "What protections does Carbon have in place to protect my personal and health information from hackers or outsiders getting into the app or into their databases that store my information?",
  "Where do they store this information?",
  "Is there any user agreement, consent forms, or any document that I can review before I even download the app and provide my personal and health information to another company?",
  "How long does Carbon keep personal/health data for?",
  "Is there anything that describes what they are doing, will do, and are allowed to do with my information?",
  "Can I delete the app AND my personal and health information and history if necessary?",
  "How long are we expected to be monitored and provide info to this app?",
  "What is a diagnostic test?",
  "What is an antibody test?",
  "How long does it take for the results to come in?",
  "How will I find out my results?",
  "How do I download the app?",
  "What happens if I test positive for the COVID-19 diagnostic test?",
  "If I test positive for the antibody test, is it safe to go back to work without a mask or safety protocols?",
  "Will Carbon Health sell my information for antibody research purposes if I test positive?",
  "What are Carbon Health pop-up locations?",
  "What kind of COVID-19 testing will be performed at the Carbon Health pop-up location?",
  "How can I get tested at pop-up locations?",
  "Can I go to Carbon Health pop-up location without an appointment?",
  "Is there a waiting area for patients at the pop-up locations?",
  "Is there parking available for patients at the pop-up locations?",
  "How is Carbon ensuring the pop-ups locations are safe and clean?",
  "Will I see a women’s health specialist?",
  "Can I make a same-day appointment?",
  "Can I schedule an in-person appointment?",
  "How can I learn more about Keep A Breast?",
  "Can your providers prescribe PEP and PrEP?",
  "Are your providers experienced with gender identity issues?",
  "Can your providers prescribe hormone therapy for gender affirming care?",
  "Which providers specialize in LGBTQ+ health?",
  "How does it work",
  "Does it work with my existing EHR",
  "What differentiates Carbon Health from competitors",
  "Are your systems HIPAA compliant",
  "Do you work with insurance",
  "Do you support video calls?",
  "What information do I need?",
  "When should I seek further care?",
  "I just tested positive for COVID-19. What do I do next?",
  "I have COVID symptoms, but my test came back negative. Does that mean I'm safe?",
  "What's the difference between isolation and quarantine?",
  "How long should I isolate myself?",
  "Do I need to tell my doctor before I go in for evaluation?",
  "Are there any medications I should avoid taking if I have COVID-19?",
  "How long does it take for antibodies to show up after being infected with the coronavirus disease?",
  "I tested positive for antibodies. Does that mean I'm immune?",
  "How do I know I have fully recovered from COVID? When is it safe to be around people?",
  "What can I expect during my COVID-19 testing appointment?",
  "Will a COVID-19 test and evaluation affect whether I’m eligible for a free wellness visit through my insurance?",
  "Can I get a COVID-19 vaccination or COVID-19 booster at the same time as a flu vaccine?",
  "Is my employee's health information secure?",
  "What type of data does the dashboard show?",
  "Is the dashboard updated with real time data?",
  "Can employees upload vaccine information if they did not get their vaccination through Carbon Health?",
  "Can an employee opt-out for religious or medical reasons?",
  "What type of test results are required prior to travel between the US and Canada?",
  "What type of documentation is required prior to boarding an Air Canada flight between the US and Canada?",
  "How do I verify proof of vaccination before re-entering Canada?",
  "What should I do if I'm having difficulty uploading a copy of my vaccination record to ArriveCAN?",
  "How to sign up and access test results",
  "What type of testing method is required for Japan?",
  "Why is Carbon Health out-of-network with my plan?",
  "My plan has coverage for out-of-network services. Why do I have to pay up-front?",
  "I have questions about my bill. Who can I talk to?",
  "How does Carbon Health come up with their self-pay prices?",
  "Why do Carbon Health's self-pay prices vary by state?",
  "How does my Student Administrative Health Fee (SAHF) work with my insurance?",
  "Will this student benefit be available at other Carbon Health locations?",
  "Do I have to change my primary care provider to use the Carbon Health Student Services location?",
  "When or how will I know if there is any patient responsibility due?",
  "Is Carbon Health at the Colleges of the Fenway location in network with my health insurance?",
  "If I'm not ready to book an appointment, can I register now?",
] as const;

export type Question = (typeof questionList)[number];

export const useFaqIndex = (items?: (Question | SectionDivider)[]): (QA | SectionDivider)[] => {
  const i18n = useTranslation();

  const faqIndex: Record<Question, QA> = useMemo(
    () => ({
      "How does partnering with Carbon Health healthcare lower costs?": {
        q: i18n.t("How does partnering with Carbon Health healthcare lower costs?"),
        a: i18n.t(
          "We developed our own electronic medical records (EMR) and tech platform making us operationally efficient. We can surface utilization data and trends in real time to help make better health cost-savings decisions on behalf of your team. Because of this, we can refer your patients to higher-quality, lower-cost specialists near them. We also have a robust preventive care program to identify and treat longer term conditions before they become serious health threats. Our app automatically reaches out for follow-up appointments and notifies our clinicians of any medical issues that need attention, to make sure patients are doing well after their visit.",
        ),
      },
      "How do you ensure student satisfaction?": {
        q: i18n.t("How do you ensure student satisfaction?"),
        a: i18n.t(
          "Students love Carbon Health, which is reflected in our NPS score of 89. We make it extremely easy to access our support staff and our clinicians via multiple channels such as text, telephone, email, and social media, or through our app. The Carbon Health app is easy to use and is compatible with iOS and Android. If a patient has an unsatisfactory experience or encounters any issues, our support team and patient navigators are there to help resolve them in a timely manner.",
        ),
      },
      "Is Carbon Health HIPAA compliant?": {
        q: i18n.t("Is Carbon Health HIPAA compliant?"),
        a: i18n.t(
          "All data you share with Carbon Health is protected by a HIPAA-compliant cloud infrastructure which is routinely audited and ever improving. Your data is always encrypted at rest and in transit.",
        ),
      },
      "Is the Carbon Health app secure?": {
        q: i18n.t("Is the Carbon Health app secure?"),
        a: i18n.t(
          "The Carbon Health apps make use of the latest developments in security architectures for both the Apple and Android platforms. The Carbon Health application is made available through the official Apple and Google Play stores. These steps ensure that the application is safely and securely installed and never tampered with.",
        ),
      },
      "What protections does Carbon Health have in place to protect personal and health information from hackers or outsiders getting into the app or into their databases that store my information?":
        {
          q: i18n.t(
            "What protections does Carbon Health have in place to protect personal and health information from hackers or outsiders getting into the app or into their databases that store my information?",
          ),
          a: i18n.t(
            "Carbon Health encrypts all of your data, ensuring that your data remains private, with protected access that keeps that data between you and your healthcare provider. Carbon Health works within a highly regulated industry, and we ensure that you are protected by enforcement of the industry’s best standards, standards that comply with HIPAA, ISO 27001, SOC 2, NIST 800-53, and other frameworks for best-of-breed security and compliance.",
          ),
        },
      "Is Carbon Health configured to meet the travel or entry requirements to any destination?": {
        q: i18n.t(
          "Is Carbon Health configured to meet the travel or entry requirements to any destination?",
        ),
        a: i18n.t(
          "Carbon Health is able to meet specific guidelines and provide the necessary forms for international travel. We also guarantee test results turnaround time of 3 hours same day or by 2pm the next day. Turnaround times and types of tests available vary by location. It is up to each patient to ensure our testing results and turnaround times are valid for international travel clearance and that any paperwork is handled separately. We recommend patients inquire directly with the airline or regulatory authorities of their travel destinations 7-10 days prior to their departure date. Many countries require testing 48 to 72 hours prior to departure.",
        ),
      },
      "How do I book COVID-19 testing with my family?": {
        q: i18n.t("How do I book COVID-19 testing with my family?"),
        a: i18n.t(
          "The first step is to create an account with Carbon Health. Each adult should create their own unique account. You can do so on our desktop site or by downloading the Carbon Health app for iOS or Android. Once your account is created, you can then add children or family members who are minors to any adult account. That way, you can schedule your family’s appointments and access their results from a single device. COVID-19 clearance exams are available for adults and children over 3 months old.",
        ),
      },
      "How do I add a child or family member who is a minor to my Carbon Health account?": {
        q: i18n.t(
          "How do I add a child or family member who is a minor to my Carbon Health account?",
        ),
        a: i18n.t(
          "If you are using the Carbon Health app: Log in to your account, select 'Settings' and click 'Switch Patient'. You will be able to add a family member there and then schedule an appointment on their account. If you are using Carbon Health on desktop: Log in to your account, select your initials in the top right corner, and click 'Switch Patient'. You will be able to add a family member there and then schedule an appointment on their behalf. Please note that each adult should create their own unique account.",
        ),
      },
      "If my partner or family is traveling with me, is it possible to get tested at the same time?":
        {
          q: i18n.t(
            "If my partner or family is traveling with me, is it possible to get tested at the same time?",
          ),
          a: i18n.t(
            "There may be times close to your appointment when you can book for your family member. A separate account needs to be created to schedule an appointment.",
          ),
        },
      "How much does COVID-19 travel testing cost?": {
        q: i18n.t("How much does COVID-19 travel testing cost?"),
        a: i18n.t(
          "The cost of COVID-19 travel testing varies by location. Costs range from {{price}} per test. COVID-19 travel testing or expedited testing is not covered by insurance; therefore, Carbon Health asks patients to pay for these tests before the test is administered. If you would like to seek reimbursement for travel testing, you can find the after-visit summary and receipt in your Carbon Health account and send them to your insurance provider.",
          {price: minMaxTravelClearancePrices},
        ),
      },
      "Can I pay with my HSA/FSA Card?": {
        q: i18n.t("Can I pay with my HSA/FSA Card?"),
        a: i18n.t("Yes - Carbon Health accepts payments via HSA and FSA cards."),
      },
      "What if my test result is invalid?": {
        q: i18n.t("What if my test result is invalid?"),
        a: i18n.t(
          "Less than one percent of the time, a test may return an invalid result. This does not mean the test was performed incorrectly but occurs in a situation where the lab device cannot report a result with confidence. If this occurs, you will be contacted for a repeat sampling at no additional cost to you.",
        ),
      },
      "How does the COVID-19 Test work?": {
        q: i18n.t("How does the COVID-19 Test work?"),
        a: i18n.t(
          "The test will be an anterior nasal swab. Swabbing will only occur to the mid nasal area and will be much more comfortable than the nasopharyngeal swab. The test is run using molecular technology to detect the presence of a virus by identifying a small section of the virus' genome, then amplifying that portion until there is enough for detection.",
        ),
      },
      "Where do I find my COVID-19 Test Results?": {
        q: i18n.t("Where do I find my COVID-19 Test Results?"),
        a: (
          <Trans>
            Once logged in to your Carbon Health account, you can access the PDF of your test
            results in the 'My Health' section by scrolling and selecting 'Lab Results'. <br />
            <a
              href="https://youtu.be/aayv3q52uys"
              className="tdu"
              target="_blank"
              rel="noopener noreferrer"
            >
              Watch
            </a>{" "}
            how to find and download on desktop . <br />
            <a
              href="https://youtu.be/sQuu7NyP2Dk"
              className="tdu"
              target="_blank"
              rel="noopener noreferrer"
            >
              Watch
            </a>{" "}
            how to find and download in mobile app .
          </Trans>
        ),
      },
      "How do I download my COVID-19 Test Results PDF?": {
        q: i18n.t("How do I download my COVID-19 Test Results PDF?"),
        a: (
          <Trans>
            Once logged in to your Carbon Health account, you can access the PDF of your test
            results in the 'My Health' section by scrolling and selecting 'Lab Results'. Once in
            'Lab Results', select the test result you would like to view, scroll to the bottom of
            the page, and select the PDF icon on the bottom left side. If you are using the Carbon
            Health mobile app, use the Export button on the bottom left to email or message your
            results PDF. If you are on the Carbon Health desktop site, use the Download button in
            the top right corner to obtain your results PDF. <br />
            <a
              href="https://youtu.be/aayv3q52uys"
              className="tdu"
              target="_blank"
              rel="noopener noreferrer"
            >
              Watch
            </a>{" "}
            how to find and download on desktop. <br />
            <a
              href="https://youtu.be/sQuu7NyP2Dk"
              className="tdu"
              target="_blank"
              rel="noopener noreferrer"
            >
              Watch
            </a>{" "}
            how to find and download in mobile app.
          </Trans>
        ),
      },
      "There are no appointments available at my desired testing location. Can I walk in?": {
        q: i18n.t(
          "There are no appointments available at my desired testing location. Can I walk in?",
        ),
        a: i18n.t("We are not accepting walk-ins at this time."),
      },
      "Is the test a nucleic acid amplification test (NAAT) test?": {
        q: i18n.t("Is the test a nucleic acid amplification test (NAAT) test?"),
        a: i18n.t("Yes."),
      },
      "How do I find the Portland Airport COVID-19 testing location?": {
        q: i18n.t("How do I find the Portland Airport COVID-19 testing location?"),
        a: (
          <div>
            <Trans>
              <p>
                It might be a bit confusing trying locate and park for this testing location. Here's
                how to find us:
              </p>

              <ul style={{listStyle: "disc"}} className="ml4 mt2 mb2">
                <li>Park in the Short Term Parking Garage @ PDX</li>
                <li>Walk across the SkyBridge on Level 4 into the Terminal</li>
                <li>Go down one level to Departures</li>
                <li>Exit out the doors near the Alaska check-in</li>
                <li>You'll find us outside at the blue tents!</li>
              </ul>
            </Trans>

            <div>
              <Image src="/static/img/v5/alaska/pdx-directions.webp" width="500" alt="directions" />
            </div>
          </div>
        ),
      },
      "Do I need to have my flight itinerary for a testing appointment?": {
        q: i18n.t("Do I need to have my flight itinerary for a testing appointment?"),
        a: i18n.t(
          "Yes, please bring your confirmed flight itinerary to your test appointment with Carbon Health.",
        ),
      },
      "What is the Health Pass?": {
        q: i18n.t("What is the Health Pass?"),
        a: i18n.t(
          "The Health Pass is a HIPAA-compliant digital vaccination card for patients to use whenever they need to provide proof of their COVID-19 vaccination. An alternative to the physical cards issued by the CDC (United States Centers for Disease Control and Prevention), the Health Pass is free for patients and currently available to everyone who has been vaccinated at a Carbon Health–powered location or a location within the Carbon Health network of private and municipal partners. We’re working to make the Health Pass available to people vaccinated by other providers.",
        ),
      },
      "Who can use the Health Pass?": {
        q: i18n.t("Who can use the Health Pass?"),
        a: i18n.t(
          "Right now, the Health Pass is available to everyone who has been vaccinated at either a Carbon Health–powered location or a location within the Carbon Health network of private and municipal partners. In the future, we plan to make the Health Pass available to everyone, regardless of where they were vaccinated.",
        ),
      },
      "What type of data does the Health Pass contain?": {
        q: i18n.t("What type of data does the Health Pass contain?"),
        a: i18n.t(
          "The Health Pass captures and displays details for single or multi-dose COVID-19 vaccinations, including the dates shot(s) were received, vaccine type and lot numbers, and vaccine site location.",
        ),
      },
      "Where do I find my Health Pass?": {
        q: i18n.t("Where do I find my Health Pass?"),
        a: (
          <Trans>
            If you received your vaccination at a <a href="faq-10">Carbon Health Location</a>, your
            Health Pass will be available when you log in to your patient account in the Carbon
            Health app, or online at https://carbonhealth.com/patients/.
          </Trans>
        ),
      },
      "How do I find my Health Pass if I did not provide an email address during sign up, or if I didn’t receive a text message after my vaccination?":
        {
          q: i18n.t(
            "How do I find my Health Pass if I did not provide an email address during sign up, or if I didn’t receive a text message after my vaccination?",
          ),
          a: (
            <ol className="lstd ml4">
              <li>
                <Trans>
                  Go to{" "}
                  <a href="https://carbonhealth.com/patients/">
                    https://carbonhealth.com/patients/
                  </a>
                  .
                </Trans>
              </li>
              <li>{i18n.t('Click the green "Sign Up" button.')}</li>
              <li>{i18n.t("Enter the phone number associated with your account.")}</li>
              <li>{i18n.t("Enter the code that is then texted to you.")}</li>
              <li>{i18n.t("Enter your date of birth.")}</li>
              <li>{i18n.t("Add your email address and create a password.")}</li>
              <li>
                {i18n.t(
                  "Add your insurance information if you have it; if not, you can choose to sign up without insurance.",
                )}
              </li>
              <li>{i18n.t("Enter your address (if prompted).")}</li>
              <li>{i18n.t('Click "View Card" to view your Health Pass.')}</li>
            </ol>
          ),
        },
      "I received my vaccine at a County of Los Angeles location before Carbon Health took over the site (typically before February 1st, 2021). Can I add my vaccine information to my Health Pass?":
        {
          q: i18n.t(
            "I received my vaccine at a County of Los Angeles location before Carbon Health took over the site (typically before February 1st, 2021). Can I add my vaccine information to my Health Pass?",
          ),
          a: (
            <>
              {i18n.t(
                "We are not yet able to pull this data into the Health Pass, but we are actively working on a solution. You are more than welcome to keep a photo of your vaccination card in our system in case you misplace your physical card. For now, you’ll need to save the vaccination card you received at the site you went to as your proof of record.",
              )}
              <br />
              <br />
              {i18n.t("To upload a photo to the Carbon Health app:")}
              <ol className="lstd mt3">
                <li>
                  {i18n.t(
                    "Open the app on your mobile device, or go to https://carbonhealth.com/patients/.",
                  )}
                </li>
                <li>{i18n.t("If you do not have an account yet, please create one.")}</li>
                <li>{i18n.t("Log in to your account.")}</li>
                <li>{i18n.t("Select the “Messages” area of your Carbon Health account.")}</li>
                <li>{i18n.t("Click on the “Documents” section.")}</li>
                <li>{i18n.t("Select the + (plus-sign) icon at the bottom left of the screen.")}</li>
                <li>
                  {i18n.t(
                    "The app will then prompt you to either take a photo or upload one from your device.",
                  )}
                </li>
                <li>
                  {i18n.t(
                    "To access your photo in the future, navigate back to your message history.",
                  )}
                </li>
              </ol>
            </>
          ),
        },
      "Does the Health Pass integrate with other applications?": {
        q: i18n.t("Does the Health Pass integrate with other applications?"),
        a: i18n.t(
          "Not yet, but we’re currently working to optimize the experience of using your Health Pass and are developing new features, including integrations with Apple Wallet, family Health Passes, and Health Pass scannable QR codes — among many other innovations — and expanding the card’s availability.",
        ),
      },
      "Can you add my vaccination record to my Health Pass if I did not get vaccinated at a Carbon Health site?":
        {
          q: i18n.t(
            "Can you add my vaccination record to my Health Pass if I did not get vaccinated at a Carbon Health site?",
          ),
          a: (
            <>
              {i18n.t(
                "If you did not receive your vaccination through Carbon Health, we’re not yet able to pull your data into the Health Pass. We are actively working on a solution! In the meantime, you’re more than welcome to keep a photo of your vaccination card in our system in case you misplace your physical card.",
              )}
              <br />
              <br />
              {i18n.t("To upload a photo to the Carbon Health app:")}
              <ol className="lstd mt3">
                <li>
                  {i18n.t(
                    "Open the app on your mobile device, or go to https://carbonhealth.com/patients/.",
                  )}
                </li>
                <li>{i18n.t("If you do not have an account yet, please create one.")}</li>
                <li>{i18n.t("Log in to your account.")}</li>
                <li>{i18n.t("Select the “Messages” area of your Carbon Health account.")}</li>
                <li>{i18n.t("Click on the “Documents” section.")}</li>
                <li>{i18n.t("Select the + (plus-sign) icon at the bottom left of the screen.")}</li>
                <li>
                  {i18n.t(
                    "The app will then prompt you to either take a photo or upload one from your device.",
                  )}
                </li>
                <li>
                  {i18n.t(
                    "To access your photo in the future, navigate back to your message history.",
                  )}
                </li>
              </ol>
            </>
          ),
        },
      "My Health Pass information is missing or wrong. What do I do?": {
        q: i18n.t("My Health Pass information is missing or wrong. What do I do?"),
        a: (
          <Trans>
            If the information on your Health Pass is not correct, please notify Carbon Health
            immediately by <a href={supportLink}>contacting support</a>. We can then notify our
            vaccine product team that your Health Pass needs to be updated. We are working on these
            updates as quickly as possible and thank you for your patience!
          </Trans>
        ),
      },
      "How much does COVID-19 testing cost?": {
        q: i18n.t("How much does COVID-19 testing cost?"),
        a: (
          <p>
            {i18n.t(
              "The following information is for COVID-19 testing only and does not apply to a regular visit to a Carbon Health location or any non-coronavirus related visits.",
            )}
            <br />
            <br />
            <Trans>
              <span className="font-ib">
                If you have insurance that is accepted by Carbon Health,{" "}
              </span>
              your insurance must cover diagnostic COVID-19 testing without patient cost sharing
              whether you are asymptomatic or symptomatic and/or do not have a confirmed exposure.
            </Trans>
            <br />
            <br />
            <Trans>
              <span className="font-ib">If your insurance is not accepted by Carbon Health, </span>
              your insurance plan must process claims as in-network to cover diagnostic COVID-19
              testing without patient cost sharing whether you are asymptomatic or symptomatic and
              do not have a confirmed exposure.
            </Trans>
            <br />
            <br />
            <Trans>
              <span className="font-ib">
                If you are not insured, there is no cost for COVID-19 testing.{" "}
              </span>
              As part of the Families First Coronavirus Response Act and the CARES Act, you will
              have to fill out a short form at the location or site at the time of your COVID-19
              testing appointment.
            </Trans>
            <br />
            <br />
            <Trans>
              <span className="font-ib">COVID-19 travel testing or expedited testing</span> is
              provided with a guaranteed turnaround time and requires the payment of a convenience
              fee. Because insurance plans are not required to cover the costs of COVID-19 tests
              associated with guaranteed turnaround times, Carbon Health asks patients to pay for
              these tests before the test is administered. See pricing for all travel testing
              options and find out where to book a test{" "}
              <a href={v5Pages.covid19TravelClearance}>here</a>.
            </Trans>
          </p>
        ),
      },
      "How much does COVID-19 testing, treatment, and vaccines cost?": {
        q: i18n.t("How much does COVID-19 testing, treatment, and vaccines cost?"),
        a: (
          <div>
            <p>
              <Trans>
                The following information is for COVID-19 testing and treatment only and does{" "}
                <strong>not</strong> apply to other Carbon Health care visits.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong>COVID-19 Vaccinations</strong>
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                There is no cost to the patients for the COVID vaccines regardless of their
                insurance, ability to pay, or immigration status. Vaccines remain available for all
                patients.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong>COVID-19 Testing & Treatment</strong>
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong className="fs18">Uninsured Patients</strong>
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                Patients may apply for Medicaid benefits and seek care from participating providers.
                Carbon Health is only a Medicaid provider at limited locations.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                Community Locations may be available near you that are offering COVID related
                services with no out of pocket costs to the uninsured. Search HRSA’s “Find a Health
                Center”
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                Patients may apply for Carbon Health’s Financial Assistance Program based on Federal
                Poverty Guidelines, if you are unable to pay for COVID related services. An approved
                application and documentation is required for program approval. Patients can email{" "}
                <a href="mailto:carbonFAP@carbonhealth.com" className="tdu">
                  carbonFAP@carbonhealth.com
                </a>{" "}
                for more information.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>Other testing options:</Trans>
            </p>
            <p>
              <Trans>
                At home testing kits are also available through{" "}
                <a
                  href="https://www.covidtests.gov/"
                  className="tdu darkBlue"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.covidtests.gov/
                </a>
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong className="fs18">Insurance Covered Patients</strong>
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong>Testing for COVID-19</strong> - Testing is still available at all Carbon
                Health locations. When the Public Health Emergency ends, insurance coverage{" "}
                <strong>may</strong> change and there may be an out of pocket responsibility due by
                the patient.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong>Treatment of COVID-19</strong> - Majority of national payers are now
                requiring standard out of pocket costs to be paid by the patient for COVID-19
                treatment. If you test positive for COVID-19, please confirm your out of pockets
                costs with your insurance company. You will be expected to pay for your out of
                pocket copays and coinsurances at the time of service.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong className="fs18">COVID-19 travel testing or expedited testing</strong>
              </Trans>
            </p>
            <p>
              <Trans>
                COVID-19 travel testing or expedited testing provided with a guaranteed turnaround
                time and requires the payment of a convenience fee. Because insurance plans are not
                required to cover the costs of COVID-19 tests associated with guaranteed turnaround
                times, Carbon Health asks patients to pay for these tests before the test is
                administered. See pricing for all travel testing options and find out where to book
                a test{" "}
                <a
                  href={v5Pages.covid19TravelClearance}
                  className="tdu darkBlue"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </Trans>
            </p>
          </div>
        ),
      },
      "How much does COVID-19 testing cost? ": {
        q: i18n.t("How much does COVID-19 testing cost?"),
        a: (
          <div>
            <p>
              <Trans>
                The following information is for COVID-19 testing only and does not apply to other
                Carbon Health care visits.
              </Trans>
            </p>

            <p className="mt4">
              <strong>
                <Trans>Uninsured Patients & COVID-19 Testing</Trans>
              </strong>
            </p>

            <p className="mt4">
              <Trans>
                COVID testing and treatment are self pay services for uninsured patients.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                Patients may enroll for Medicaid benefits and seek care from participating
                providers. Carbon Health is only a Medicaid provider at limited locations.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                Community Locations may be available near you that are offering COVID related
                services with no out of pocket costs to the uninsured. Search HRSA’s “Find a Health
                Center”
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                Patients may apply for Carbon Health’s Financial Assistance Program based on Federal
                Poverty Guidelines, if you are unable to pay for COVID related services. An approved
                application and documentation is required for program approval. Patients can email
                carbonFAP@carbonhealth.com for more information.
              </Trans>
            </p>

            <p className="mt4">
              <strong>
                <Trans>Insurance Covered Patients - COVID-19 Testing</Trans>
              </strong>
            </p>

            <p className="mt4">
              <Trans>
                <strong>Testing for COVID-19</strong> - Testing is still available at all Carbon
                Health locations. When the Public Health Emergency ends, insurance coverage{" "}
                <strong>may</strong> change and there may be an out of pocket responsibility due by
                the patient.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>
                <strong>Treatment of COVID-19</strong> - Majority of national payers are now
                requiring standard out of pocket costs to be paid by the patient for COVID-19
                treatment. If you test positive for COVID-19, please confirm your out of pockets
                costs with your insurance company. You will be expected to pay for your out of
                pocket copays and coinsurances at the time of service.
              </Trans>
            </p>

            <p className="mt4">
              <Trans>Other options for testing:</Trans>
            </p>
            <p className="mt4">
              <Trans>
                At home testing is available directly through your insurance payer and designated
                retail pharmacies at no cost to the patient.
              </Trans>
            </p>
            <p className="mt4">
              <Trans>
                At home testing kits are also available through{" "}
                <a
                  href="https://www.covidtests.gov/"
                  className="tdu darkBlue"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://www.covidtests.gov/
                </a>
              </Trans>
            </p>
          </div>
        ),
      },
      "Do you accept my insurance?": {
        q: i18n.t("Do you accept my insurance?"),
        a: i18n.t(
          "While we are contracted with most insurance carriers, PPOs, and Medicare, we always recommend giving your health insurance plan provider a call to confirm your coverage and benefits. The number can be found on your insurance card. Please note that Medicare and Medi-Cal are only accepted in select locations. Out-of-network service and benefit coverage can vary based on geographic location or between an urgent or primary care visit.",
        ),
      },
      "How much will my co-pay be?": {
        q: i18n.t("How much will my co-pay be?"),
        a: i18n.t(
          "To get your specific copay cost amount for your visit, we'll need to see your insurance card and verify the insurance carrier. We recommend that you call the member's services number on the back of your insurance card and ask about coverage and co-payment fees for urgent care visits. Co-pays for an urgent care visit will typically be different than the co-pay amount for a primary care visit. If you don't have time to call your insurance, we can determine the co-pay amount if your plan and coverage is verified upon check-in to your visit, whether you are visiting Carbon Health for urgent care or primary care.",
        ),
      },
      "What are co-pays?": {
        q: i18n.t("What are co-pays?"),
        a: i18n.t(
          "A co-pay, short for copayment, is a fixed amount a healthcare beneficiary pays for covered medical services. The remaining balance is covered by the patient's insurance company. Co-pays typically vary for different services within the same plans, particularly when they involve services that are considered essential or routine and others that are considered to be less routine or in the domain of a specialist. Co-pays for standard doctor visits are typically lower than those for specialists. Note that copays for emergency room visits tend to be the highest.",
        ),
      },
      "What are deductibles?": {
        q: i18n.t("What are deductibles?"),
        a: i18n.t(
          "A deductible is a fixed amount a patient must pay each year before their health insurance benefits begin to cover the costs. After meeting a deductible, beneficiaries typically pay coinsurance—a certain percentage of costs—for any services that are covered by the plan. They continue to pay the coinsurance until they meet their out-of-pocket maximum for the year.",
        ),
      },
      "What does it mean if my insurance is not verified?": {
        q: i18n.t("What does it mean if my insurance is not verified?"),
        a: (
          <Trans>
            <p>
              When you come in for your visit, our team will take your insurance information and
              quickly check the status of your coverage with your insurance provider. There may be
              times when an insurance plan can’t be verified. For example, your insurance
              information may be inaccurate or out of date, or the insurance company’s eligibility
              system may not respond. In cases where we are unable to verify your insurance at the
              time of your visit, we will charge you the flat rate for this service. These rates are
              standard and vary by treatment type. You can find them
              <Link href={v5Pages.insurance} className="underline ml-1">
                https://carbonhealth.com/insurance-pricing
              </Link>
              .
            </p>

            <p className="mt-8">
              As a courtesy, we will submit your claim to your insurance company after your visit.
              Please check the Carbon Health app for a status on your bill, which may be adjusted
              based on total treatment costs and your insurance benefits.
            </p>
          </Trans>
        ),
      },
      "How do I add my insurance if I'm on my family's plan?": {
        q: i18n.t("How do I add my insurance if I'm on my family's plan?"),
        a: i18n.t(
          'If you are on your family\'s insurance plan as a dependent, after you scan the insurance card, select "Family Member" Instead of "This is me." If you are not given the option to select "Family Member" please contact your insurance and confirm that you are marked as a dependent on their plan. Keep in mind that certain information from the insurance card is auto-populated into your chart. If "This is me" is selected, or if you manually enter a family member\'s name instead of yours, your name will be incorrect in your chart. If this is the case please re-enter your insurance information.',
        ),
      },
      "When will I get my bill?": {
        q: i18n.t("When will I get my bill?"),
        a: i18n.t(
          'If your insurance was verified during your visit, Carbon Health bills your insurance about three days after your appointment. If you have outside lab work done you will be billed completely separately by LabCorp or Quest. If the lab and test is performed entirely on-site it will appear on your Carbon Health bill. Your insurance may pay all or portion of your bill, depending on your benefit plan and your deductible. At some point you will receive an EOB (Explanation of Benefits) from your insurance, directly. This is not your bill but an explanation of what insurance paid/covered and what you may owe. You can always check the status of your bill in your Carbon Health app by tapping your profile in the lower right then "Bills". You can track Due, Paid and Sent/Waiting. For a full breakdown you can tap the information (i) icon next to the appointment date. If you have any patient responsibility once your claim has been processed by your insurance, you will receive an invoice via email from billing@carbonhealth.com and an SMS alert when your bill is ready. You can pay securely and instantly by tapping Pay Now in the email. You can also pay directly in the app. Please contact us at billing@carbonhealth.com if it\'s been more than two months since your appointment and you still have not received a bill, or if you have any billing questions at all. Happy to help!',
        ),
      },
      "My credit card was charged by 'direct urgent care'": {
        q: i18n.t("My credit card was charged by 'direct urgent care'"),
        a: i18n.t(
          "If you were a patient at one our Direct Urgent Care locations in the Bay Area and have any billing questions or concerns please email us at billing@carbonhealth.com anytime or call us at 1-415-200-2133. Our typical phone hours are 9:30am-5pm PT, Monday through Friday.",
        ),
      },
      "How to book an in-person visit": {
        q: i18n.t("How to book an in-person visit"),
        a: (
          <p>
            <p>
              <b>{i18n.t("Step 1: ")}</b>
              <Trans>
                <a href="https://carbonhealth.com/download?utm_source=medium&utm_medium=blog&utm_campaign=virtual-care-how-to">
                  Download the Carbon Health app
                </a>{" "}
                for IOS or Android.
              </Trans>
            </p>
            <p>
              <b>{i18n.t("Step 2: ")}</b>
              {i18n.t(`Open the app and tap the green "Get Care" button.`)}
            </p>
            <p>
              {i18n.t("Then, choose from the following options:")}
              <ul>
                <li>
                  <a href="https://carbonhealth.com/virtual-care/telehealth">
                    {i18n.t("Urgent Care")}
                  </a>
                  {i18n.t(
                    ": ideal for immediate medical attention for injuries, infections, or any unplanned medical need.",
                  )}
                </li>
                <li>
                  <a href="https://carbonhealth.com/get-care/primary-care?utm_source=medium&utm_medium=blog&utm_campaign=virtual-care-how-to">
                    {i18n.t("Primary Care")}
                  </a>
                  {i18n.t(
                    ": ideal for wellness and preventive care for long-term health needs like vaccinations, physicals, and medications.",
                  )}
                </li>
              </ul>
            </p>
            <p>
              <b>{i18n.t("Step 3: ")}</b>
              {i18n.t("Select your condition from the menu or search for your specific symptom.")}
            </p>
            <p>
              <b>{i18n.t("Step 4: ")}</b>
              {i18n.t("Choose your appointment time.")}
            </p>
            <p>
              <b>{i18n.t("Quick Tip:")}</b>{" "}
              {i18n.t(
                "Virtual Care appointments can be made up to 10 minutes prior to the start of your virtual visit. Just be sure you complete your registration information before your appointment time, or your appointment will be rescheduled.",
              )}
            </p>
            <p>
              <b>{i18n.t("Step 5: ")}</b>
              {i18n.t(
                "Create your account if you haven’t already. If you already have an account, select an option for how you’d like to pay for your visit using insurance or with a credit card.",
              )}
            </p>
            <p>
              <b>{i18n.t("Step 6: ")}</b>
              {i18n.t(`Read over and sign the "Consent to Treat" form.`)}
            </p>
            <p>
              <b>{i18n.t("Step 7: ")}</b>
              {i18n.t(
                "Confirm your appointment details. Once confirmed, you’ll answer a few questions from our team to give them more background on your specific health issue.",
              )}
            </p>
          </p>
        ),
      },
      "Can I select a new primary care provider?": {
        q: i18n.t("Can I select a new primary care provider?"),
        a: i18n.t(
          "You can change providers when you book your next appointment in the Carbon Health app. The good news is your medical records will remain secure and confidential and will be available to your new Carbon health provider so there is no work needed on your end at this time.",
        ),
      },
      "Why is the location now being run by Carbon Health?": {
        q: i18n.t("Why is the location now being run by Carbon Health?"),
        a: i18n.t(
          "Carbon Health has been a longtime partner of Saint Mary’s and we have already been using their technology and app in our urgent care locations. Some of the exciting changes coming up include access to Carbon Health’s virtual and in-person primary and urgent care offerings. Plus, with the Carbon Health app you can now get access to virtual primary and urgent care, book appointments, chat with your provider, and view your medical records.",
        ),
      },
      "Will my insurance be out of network now?": {
        q: i18n.t("Will my insurance be out of network now?"),
        a: i18n.t(
          "There will be no changes to the types of insurance accepted. If your insurance is currently accepted, then it will continue to be accepted.",
        ),
      },
      "How do I book an appointment?": {
        q: i18n.t("How do I book an appointment?"),
        a: (
          <Trans>
            First, you need to download the Carbon Health app on your phone. After entering your
            insurance information and creating a profile, you can easily book appointments with your
            provider of choice. If you don’t have a smartphone, you can also book an appointment by
            clicking <a href="/locations">here</a>.
          </Trans>
        ),
      },
      "Are there any membership fees?": {
        q: i18n.t("Are there any membership fees?"),
        a: i18n.t(
          "Carbon Health has no membership fees, meaning that everyone can get access to their high-quality care.",
        ),
      },
      "Who are the people I will be speaking to through the virtual service?": {
        q: i18n.t("Who are the people I will be speaking to through the virtual service?"),
        a: i18n.t(
          "A highly qualified team of Medical Directors (MD), Physicians Assistants (PA), and Nurse Practitioners (NP).",
        ),
      },
      "Who is eligible for serological antibody testing?": {
        q: i18n.t("Who is eligible for serological antibody testing?"),
        a: i18n.t(
          "Anyone who needs to know whether they have ever been infected are appropriate candidates for testing. Ultimately, testing is determined by your clinician.",
        ),
      },
      "How can I get an antibody test?": {
        q: i18n.t("How can I get an antibody test?"),
        a: i18n.t(
          "To get tested, schedule an appointment at a Carbon Health location for an assessment using the COVID-19 Concerns appointment reason.",
        ),
      },
      "How long does it take to get serological antibody results back?": {
        q: i18n.t("How long does it take to get serological antibody results back?"),
        a: i18n.t(
          "Results may take 3 to 4 days to process. However, this will vary based on the volume of tests received by the labs. Once the results are in, you will receive a call or a message from one of our providers with next steps.",
        ),
      },
      "What does a negative antibody result indicate?": {
        q: i18n.t("What does a negative antibody result indicate?"),
        a: i18n.t(
          "A negative serologic test result indicates that you have not developed detectable antibodies at the time of testing. This may be due to having testing performed too early in the course of COVID-19, the absence of exposure to the virus, or the lack of an adequate immune response. A negative test does not completely rule out a past or present infection with SARS-CoV-2 virus and correlation with a diagnostic test may be considered to look for an active infection. A negative result should not be used to influence social distancing, hand hygiene, or use of protective equipment decisions.",
        ),
      },
      "Why is Carbon Health offering the antibody test?": {
        q: i18n.t("Why is Carbon Health offering the antibody test?"),
        a: i18n.t(
          "The COVID-19 serology test does not diagnose you with an active SARS-CoV-2 virus infection, but it can provide some information about whether a patient has been exposed to the SARS-CoV-2 virus at least 4 days or more ago. Serology testing along with each individual's clinical scenario can provide some information to patients particularly when the test is negative. In certain populations, this information can help them better understand what their exposures have been.",
        ),
      },
      "What does it mean for me if I test positive with the antibody test?": {
        q: i18n.t("What does it mean for me if I test positive with the antibody test?"),
        a: i18n.t(
          "A positive serologic test result indicates that you have likely produced an immune response to the SARS-CoV2 virus (COVID) or one of a subset of the other types of coronaviruses (HKU1, NL63, OC43 or 229E). A positive test result does not exclude an active infection and it is not known if the antibody confers clinical immunity. It is very important to keep in mind that this test does not indicate whether you currently have an active infection or whether you are infectious. A PCR test may be recommended to help identify an active infection. Keep in mind that patients can remain infectious for many weeks after they are infected with the virus. A positive test means you may or may not pass the infection along to others. Based on current evidence, it is unclear whether the presence of these antibodies result in immunity. Decisions about ongoing monitoring or return to normal activities should be made after discussing with your clinician.",
        ),
      },
      "Do I need to retake the antibody test every few weeks to continue to confirm immunity?": {
        q: i18n.t(
          "Do I need to retake the antibody test every few weeks to continue to confirm immunity?",
        ),
        a: i18n.t(
          "Since the serology test is relevant to potential infections that have occurred 4 or more days after your illness, repeat testing is unnecessary unless you develop a new potential infection. These tests are most accurate when you test negative though suggesting that you have not had exposure to coronavirus.",
        ),
      },
      "What is the false-negative rate for the antibody test?": {
        q: i18n.t("What is the false-negative rate for the antibody test?"),
        a: i18n.t(
          "Although the exact number is not known, the range that LabCorp and Quest Diagnostics have published depends on the number of days after your infection you are tested. The longer the duration, the fewer false negatives. This can range from 3-15%.",
        ),
      },
      "How much does a COVID-19 Clearance Exam cost?": {
        q: i18n.t("How much does a COVID-19 Clearance Exam cost?"),
        a: (
          <Trans>
            <p>
              If you have insurance, for example Kaiser HMO or Medi-Cal, there is no fee or cost to
              you as a patient. You will need to supply Carbon Health with your insurance details,
              preferably when you book your appointment online, or via the Carbon Health app.
            </p>
            <p>
              If you are not insured, there is no fee or cost to you as the patient. As part of the
              Families First and CARES Act, you will have to fill out a short form at the location
              at the time of your COVID-19 Clearance Exam appointment. The new FFCRA and CARES Acts
              have made funding available to allow Coronavirus (COVID-19 ) related care accessible
              to all Americans including those uninsured. We still will need to file the testing
              visit to the government.
            </p>
          </Trans>
        ),
      },
      "When do I need to schedule a COVID-19 Clearance Exam?": {
        q: i18n.t("When do I need to schedule a COVID-19 Clearance Exam?"),
        a: i18n.t(
          "Lab result turnaround times can vary so we advise you book your appointment 7 days prior to your procedure date. Space is limited so book as early as possible to avoid delays on your main procedure.",
        ),
      },
      "When can I expect the results of my COVID-19 Clearance Exam?": {
        q: i18n.t("When can I expect the results of my COVID-19 Clearance Exam?"),
        a: i18n.t(
          "We send out specimens 7 days a week and see results from our lab partners come in continuously. Typically results are available within 72 hours of testing, however this is not guaranteed so book your appointment early.",
        ),
      },
      "I'm not currently in the location where my procedure will be taking place. Can I get a COVID-19 Clearance Exam elsewhere?":
        {
          q: i18n.t(
            "I'm not currently in the location where my procedure will be taking place. Can I get a COVID-19 Clearance Exam elsewhere?",
          ),
          a: i18n.t(
            "Carbon Health has locations across California. You are welcome to book an exam at any location near you.",
          ),
        },
      "How much does it cost to enroll in COVID Positive Care?": {
        q: i18n.t("How much does it cost to enroll in COVID Positive Care?"),
        a: i18n.t(
          "There is no cost to patients enrolling in the program. COVID Positive Care is fully covered by insurance, and for uninsured patients, currently covered by the CARES Act.",
        ),
      },
      "Why is Carbon Health offering this program?": {
        q: i18n.t("Why is Carbon Health offering this program?"),
        a: i18n.t(
          "Currently there is a tremendous gap between executing a COVID-19 test and patients realizing meaningful care. We can do better. We want to alleviate the fear and anxiety people are feeling when they are left to fend for themselves while minimizing hospitalizations. We’re doing this by catching signs of deterioration early before conditions become dire. Our goal is to provide every COVID Positive Care patient with a pathway to clearance and reintegration as well as a little peace of mind via exceptional care.",
        ),
      },
      "What happens if a patient develops COVID-related complications while in the program?": {
        q: i18n.t(
          "What happens if a patient develops COVID-related complications while in the program?",
        ),
        a: i18n.t(
          "We continuously monitor for complications in the program with timely virtual visits, symptom tracking, and devices for those who qualify. In addition, if you should need to connect to a provider, Virtual Care appointments can be made in the app or online. Carbon locations also accept walk-ins, and support and clinical staff are available via chat in our app.",
        ),
      },
      "Will Carbon Health be providing repeat testing for patients enrolled in the program?": {
        q: i18n.t(
          "Will Carbon Health be providing repeat testing for patients enrolled in the program?",
        ),
        a: i18n.t(
          "There are certain patient groups that may require repeat COVID-19 testing due to high-risk exposure for clearance. This may include immunocompromised patients, those who live with high-risk individuals who are immunocompromised or have multiple serious medical conditions, and for those going back to work/school if required. The patient will discuss whether they require repeat testing for clearance with a provider during their visit. Due to CDC guidelines, ordering repeat tests is not available for individuals who are not considered high-risk.",
        ),
      },
      "How are mental health referrals handled?": {
        q: i18n.t("How are mental health referrals handled?"),
        a: i18n.t(
          "Carbon Health currently offers mental health services to patients in California, however, we are more than happy to make referrals to local providers for patients outside of California. Carbon Health providers are able to make referrals to the Big Health app or Sleepio app at no cost to all patients. Big Health is a digital therapeutic for worry and anxiety. Sleepio is a digital therapeutic for poor sleep.",
        ),
      },
      "How does COVID Ready work?": {
        q: i18n.t("How does COVID Ready work?"),
        a: (
          <Trans>
            <p>
              Employers signed up for COVID Ready will share an eligibility file so that our support
              team can enroll members and upload them into the customized employer dashboard.
              Employees will sign up for the COVID Ready program through a unique link and complete
              the onboarding process. Employers will be able to track their employee's test results
              in a customized employer dashboard. The app employees use is the same one that Carbon
              Health patients have used for years.
            </p>
            <br />
            <p>
              Employees are able to update their status through Carbon Health's in-app COVID-19
              assessment tool. When necessary, employees will be referred to a virtual or on-site
              visit, that may or may not include a COVID-19 test.
            </p>
          </Trans>
        ),
      },
      "How do employees onboard through the Carbon Health app?": {
        q: i18n.t("How do employees onboard through the Carbon Health app?"),
        a: i18n.t(
          "Employees receive a notification via email or text that prompts them to register.",
        ),
      },
      "Which test is used to test employees for COVID-19?": {
        q: i18n.t("Which test is used to test employees for COVID-19?"),
        a: i18n.t(
          "Carbon Health's clinicians will determine which test to offer based on the employee's circumstances, such as risk, proximity to a testing location, and severity of symptoms. Carbon Health works with a wide variety of labs to test employees for COVID-19.",
        ),
      },
      "Can employers see an employee's private medical information?": {
        q: i18n.t("Can employers see an employee's private medical information?"),
        a: i18n.t(
          "Employers are only able to see COVID-19 test data and related data (such as how often an employee is checking in, if they are ready to return to work, and so on) about employees. Non COVID-19 data will not be visible to employers.",
        ),
      },
      "What employee information is being tracked?": {
        q: i18n.t("What employee information is being tracked?"),
        a: (
          <Trans>
            <p>
              Employers will be able to track an employee's enrollment date, vaccination status, and
              COVID-19 test results.
            </p>
            <br />

            <p>Here is a list of the data an employer can see:</p>
            <ul style={{listStyle: "disc", marginLeft: "1.5rem"}}>
              <li>Enrollment date</li>
              <li>Daily check ins completed (Y/N & Aggregate Count)</li>
              <li>COVID-19 Test Results</li>
              <li>Vaccination Status</li>
            </ul>
          </Trans>
        ),
      },
      "How does Carbon perform Employee Contact Tracing?": {
        q: i18n.t("How does Carbon perform Employee Contact Tracing?"),
        a: i18n.t(
          "For employees who test positive, Carbon Health clinicians run a comprehensive contact tracing program. Trained Carbon Health clinicians help a positive patient understand the risks to them and those around them and walk them through the criteria to identify potentially exposed contacts. Carbon Health clinicians will then contact the exposed individuals and ensure they get the care they need, including directing them to testing.",
        ),
      },
      "How does Carbon do Workplace Contact Tracing?": {
        q: i18n.t("How does Carbon do Workplace Contact Tracing?"),
        a: i18n.t(
          "When an employee tests positive, Carbon Health clinicians contact the program administrator at your company and help them understand the criteria for exposure, identify additional employees who might have been exposed and contact them and ensure they receive the testing and care that they need.",
        ),
      },
      "What protections does Carbon have in place to protect my personal and health information from hackers or outsiders getting into the app or into their databases that store my information?":
        {
          q: i18n.t(
            "What protections does Carbon have in place to protect my personal and health information from hackers or outsiders getting into the app or into their databases that store my information?",
          ),
          a: i18n.t(
            "Carbon Health encrypts all of your data in transit and at rest, ensuring that your data remains private, with protected access that keeps that data between you and your healthcare provider. Carbon Health works within a highly regulated industry, and we ensure that you are protected by enforcement of the industry's best standards, standards that comply with HIPAA, ISO 27001, SOC 2, NIST 800-53, and other frameworks for best of breed security and compliance.",
          ),
        },
      "Is employee data protected?": {
        q: i18n.t("Is employee data protected?"),
        a: (
          <Trans>
            Carbon Health follows strict HIPAA compliance regulations to ensure the safety and
            protection of employee health data, Carbon Health's privacy policy (
            <a href={v5Pages.privacy} className="tdu">
              https://carbonhealth.com/privacy-policy
            </a>
            ). Employees' COVID-19 test results will be shared with employers, and employees will be
            clearly and frequently notified prior to that data being shared.
          </Trans>
        ),
      },
      "Can employees control what data is shared?": {
        q: i18n.t("Can employees control what data is shared?"),
        a: i18n.t(
          "Employees will have control over which information they share, such as work notes generated by Carbon clinicians.",
        ),
      },
      "Where do they store this information?": {
        q: i18n.t("Where do they store this information?"),
        a: i18n.t(
          "All patient data and all medical data used by Carbon Health is stored only within Carbon Health managed systems. Your medical data belongs to you. No private and/or identifying data you provide will ever be sold.",
        ),
      },
      "What is a diagnostic test?": {
        q: i18n.t("What is a diagnostic test?"),
        a: i18n.t(
          "Diagnostic tests are used by doctors to diagnose people with or without symptoms related to COVID-19. Specimen samples are taken from the patient’s nose or throat with results in 5-7 days.",
        ),
      },
      "What is an antibody test?": {
        q: i18n.t("What is an antibody test?"),
        a: i18n.t(
          "Antibody tests check your blood by looking for antibodies, which may tell you if you had a past infection with the virus that causes COVID-19. You will get your results in 2-3 days.",
        ),
      },
      "How long does it take for the results to come in?": {
        q: i18n.t("How long does it take for the results to come in?"),
        a: i18n.t(
          "COVID-19 diagnostic test results typically take five to seven days to process, under normal conditions. COVID-19 antibody (serology) test results typically take about three to four days.",
        ),
      },
      "How will I find out my results?": {
        q: i18n.t("How will I find out my results?"),
        a: i18n.t(
          "You will receive your results via Carbon Health mobile app within 5-7 days. We highly recommend you download the app before your appointment so you could easily access your test results.",
        ),
      },
      "Is there any user agreement, consent forms, or any document that I can review before I even download the app and provide my personal and health information to another company?":
        {
          q: i18n.t(
            "Is there any user agreement, consent forms, or any document that I can review before I even download the app and provide my personal and health information to another company?",
          ),
          a: (
            <Trans>
              The Carbon Health Privacy Policy (
              <a href={v5Pages.privacy}>https://carbonhealth.com/privacy-policy</a>) and Carbon
              Health Terms of Use (<a href={v5Pages.terms}>https://carbonhealth.com/terms-of-use</a>
              ) are publicly available on our website to ensure you may review exactly how Carbon
              Health works to protect your privacy and to ensure your privacy remains protected.
            </Trans>
          ),
        },
      "How long does Carbon keep personal/health data for?": {
        q: i18n.t("How long does Carbon keep personal/health data for?"),
        a: i18n.t(
          "Under federal and state rules and regulations in the United States, we are not allowed to delete medical records for a period of time. Your medical records are highly regulated and highly secure. For Medicare patients, CMS (Centers for Medicare & Medicaid Services) requires that we retain patient records indefinitely. Please note, that the same laws written to ensure your medical data remains available to support your healthcare, also ensure your healthcare data is kept secure and protected under threat of financial and criminal penalty.",
        ),
      },
      "Is there anything that describes what they are doing, will do, and are allowed to do with my information?":
        {
          q: i18n.t(
            "Is there anything that describes what they are doing, will do, and are allowed to do with my information?",
          ),
          a: i18n.t(
            "First and foremost, Carbon Health will use your data to provide you the best possible healthcare we can provide: To ensure that you receive the direction and treatment appropriate for any health condition that you may be experiencing. For this purpose, your data is entirely private between you and Carbon Health. If you choose to sign a waiver authorizing a release of healthcare information to someone that is not Carbon Health (to release your COVID-19 test results to your employer for example) that release will explicitly define the data you are authorizing for release (First Name, Last Name, and whether the test was Positive or Negative for example) and to whom you are releasing (specific members of your employers' HR team for example) and no other data collected can or will be shared. Carbon Health wants to take care of you and your data: We will ask you about underlying conditions, your general health, and things that will help us provide you the best care we know how, but none of that will be shared unless you ask us to share it.",
          ),
        },
      "Can I delete the app AND my personal and health information and history if necessary?": {
        q: i18n.t(
          "Can I delete the app AND my personal and health information and history if necessary?",
        ),
        a: i18n.t(
          "You may absolutely delete the application at any time. Carbon Health is required by law to protect and retain your health information as shared with us. The laws requiring this data be kept were written to ensure medical treatment can be reviewed, and health records remain available for the period required. The laws that ensure this data is available for your future healthcare needs include protections that ensure your data remains private, and that the data is only accessible to you and your designated providers.",
        ),
      },
      "How long are we expected to be monitored and provide info to this app?": {
        q: i18n.t("How long are we expected to be monitored and provide info to this app?"),
        a: i18n.t(
          "You may choose to opt-out of this program, and to rescind authorization to share your test results at any time. The choice to share or not share your data with your employer is entirely yours to make, and you will never lose the right to change your decision.",
        ),
      },
      "How do I download the app?": {
        q: i18n.t("How do I download the app?"),
        a: (
          <Trans>
            The Carbon Health app is available through the App Store and Google Play. You can also
            visit{" "}
            <a href={v5Pages.download} className="tdu">
              carbonhealth.com/download
            </a>{" "}
            directly and download the app.,
          </Trans>
        ),
      },
      "What happens if I test positive for the COVID-19 diagnostic test?": {
        q: i18n.t("What happens if I test positive for the COVID-19 diagnostic test?"),
        a: i18n.t(
          "Our clinical staff will walk you through the next steps. Depending on your symptoms, you might be advised to go to the emergency room or self-quarantine at home. We will check in with you daily to monitor your progress.",
        ),
      },
      "If I test positive for the antibody test, is it safe to go back to work without a mask or safety protocols?":
        {
          q: i18n.t(
            "If I test positive for the antibody test, is it safe to go back to work without a mask or safety protocols?",
          ),
          a: i18n.t(
            "Based on current evidence, it is unclear whether the presence of SARS-CoV2 virus (COVID) antibodies results in immunity. Decisions about ongoing monitoring or protocols surrounding activities should be made after discussing with your clinician.",
          ),
        },
      "Will Carbon Health sell my information for antibody research purposes if I test positive?": {
        q: i18n.t(
          "Will Carbon Health sell my information for antibody research purposes if I test positive?",
        ),
        a: i18n.t(
          "No, your healthcare is between you and your care team and is always confidential.",
        ),
      },
      "What are Carbon Health pop-up locations?": {
        q: i18n.t("What are Carbon Health pop-up locations?"),
        a: i18n.t(
          "Carbon Health’s pop-up locations are permanent point-of-care facilities that marry our unique brand of virtual and in-person care. These state-of-the-art locations may not look like your average doctors’ office, but they function like one.",
        ),
      },
      "What kind of COVID-19 testing will be performed at the Carbon Health pop-up location?": {
        q: i18n.t(
          "What kind of COVID-19 testing will be performed at the Carbon Health pop-up location?",
        ),
        a: i18n.t(
          "Carbon Health pop-up locations will perform COVID-19 diagnostic and antibody test.",
        ),
      },
      "How can I get tested at pop-up locations?": {
        q: i18n.t("How can I get tested at pop-up locations?"),
        a: i18n.t(
          "It’s required to book an appointment at carbonhealth.com before coming to the pop-up COVID-19 testing location. This is so we may safely guide you into the location and reduce your risk of exposure. Once you’ve booked your appointment online, go to the office during your scheduled time. Once there, our clinicians will guide you through the next steps. Once inside the pop-up location, you will have a virtual visit with one of our providers. A nurse will be on-site to take your samples and answer any questions you might have.",
        ),
      },
      "Can I go to Carbon Health pop-up location without an appointment?": {
        q: i18n.t("Can I go to Carbon Health pop-up location without an appointment?"),
        a: i18n.t(
          "Pop-ups locations are not available for walk-in visits. Patients can schedule an appointment through carbonhealth.com or through the Carbon Health app.",
        ),
      },
      "Is there a waiting area for patients at the pop-up locations?": {
        q: i18n.t("Is there a waiting area for patients at the pop-up locations?"),
        a: i18n.t(
          "Carbon Health pop-up locations do not have a lobby. Patients will be seen by appointment and will be able to wait outside the pop-up location or in their cars until it is their turn to enter. There will be a check-in tent in front of the trailer.",
        ),
      },
      "Is there parking available for patients at the pop-up locations?": {
        q: i18n.t("Is there parking available for patients at the pop-up locations?"),
        a: i18n.t("There will be reserved parking lots for Carbon Health patients."),
      },
      "How is Carbon ensuring the pop-ups locations are safe and clean?": {
        q: i18n.t("How is Carbon ensuring the pop-ups locations are safe and clean?"),
        a: (
          <Trans>
            1. All on-site employees are screened daily.
            <br />
            2. Team members wear protective equipment at all times and sanitize their hands after
            handling any patient’s belongings.
            <br />
            3. All surfaces are cleaned hourly and in between patient visits.
            <br />
            4. Clinics are deep cleaned at the end of each day.
            <br />
            5. Social distancing protocols in place at all times.
            <br />
          </Trans>
        ),
      },
      "Will I see a women’s health specialist?": {
        q: i18n.t("Will I see a women’s health specialist?"),
        a: i18n.t(
          "All of our providers are well versed in women's health issues and will be able to provide a consultation and offer you assistance on next steps.",
        ),
      },
      "Can I make a same-day appointment?": {
        q: i18n.t("Can I make a same-day appointment?"),
        a: i18n.t(
          "Virtual Care appointments can be made up to 10 minutes prior to the start of your virtual visit. Just be sure you complete your registration information before your appointment time, or your appointment will be rescheduled.",
        ),
      },
      "Can I schedule an in-person appointment?": {
        q: i18n.t("Can I schedule an in-person appointment?"),
        a: i18n.t(
          "You will be automatically directed to a virtual appointment booking but you are always more than welcome to visit any of our locations if one is nearby and in your network.",
        ),
      },
      "How can I learn more about Keep A Breast?": {
        q: i18n.t("How can I learn more about Keep A Breast?"),
        a: (
          <Trans>
            <a className="bold green" href="http://keep-a-breast.org">
              Click here{" "}
            </a>
            to learn more!
          </Trans>
        ),
      },
      "Can your providers prescribe PEP and PrEP?": {
        q: i18n.t("Can your providers prescribe PEP and PrEP?"),
        a: i18n.t(
          "All of our primary care providers are qualified to prescribe these medications. It should be noted, however, that these medications do not protect against all STIs and doesn’t eliminate the need for safe sex practices. Taking these factors into account, along with your personal history and current health status, your provider will work to create a treatment plan for you.",
        ),
      },
      "Are your providers experienced with gender identity issues?": {
        q: i18n.t("Are your providers experienced with gender identity issues?"),
        a: i18n.t(
          "We recognize that binary standards of gender aren't sufficient for each of our patients. While we require your legal name and gender for insurance purposes, we offer tools within our app and website to share your gender identity, preferred pronouns, and preferred name. Our providers will use your preferred name during and between your visits. Additionally, our locations are always equipped with gender-neutral bathrooms. Carbon Health continuously strives to improve accessibility and inclusivity, so our patients should feel free to provide open, honest feedback to our providers and staff.",
        ),
      },
      "Can your providers prescribe hormone therapy for gender affirming care?": {
        q: i18n.t("Can your providers prescribe hormone therapy for gender affirming care?"),
        a: i18n.t(
          "Currently, we provide hormone management by referral, but plan to offer these services for our patients soon.",
        ),
      },
      "Which providers specialize in LGBTQ+ health?": {
        q: i18n.t("Which providers specialize in LGBTQ+ health?"),
        a: i18n.t(
          "All the providers in Carbon Health’s primary care program are highly trained in the best and most up to date evidenced based practices and provide culturally competent care.",
        ),
      },
      "How does it work": {
        q: i18n.t("How does it work"),
        a: i18n.t(
          "Our team manages the locations on behalf of our partners to deliver a high-quality access point, while also improving financial performance.",
        ),
      },
      "Does it work with my existing EHR": {
        q: i18n.t("Does it work with my existing EHR"),
        a: i18n.t("Yes, our technology integrates with many leading EHRs."),
      },
      "What differentiates Carbon Health from competitors": {
        q: i18n.t("What differentiates Carbon Health from competitors"),
        a: i18n.t(
          "We are the only company to build all of the operating technology from the ground up. This allows us to reimagine the healthcare delivery experience, and optimize for highly profitable and efficient locations. ",
        ),
      },
      "Are your systems HIPAA compliant": {
        q: i18n.t("Are your systems HIPAA compliant"),
        a: i18n.t("Yes, all of our technology platforms are HIPAA compliant. "),
      },
      "Do you work with insurance": {
        q: i18n.t("Do you work with insurance"),
        a: i18n.t(
          "Yes, we are licensed with all major insurers and are able to leverage our partners' medical groups for operations.",
        ),
      },
      "Do you support video calls?": {
        q: i18n.t("Do you support video calls?"),
        a: i18n.t(
          "Yes, if it's needed we'll have the clinician video with you. However, usually just messaging back and forth should suffice.",
        ),
      },
      "What information do I need?": {
        q: i18n.t("What information do I need?"),
        a: i18n.t("A government issued ID and a photo of yourself for verification purposes"),
      },
      "When should I seek further care?": {
        q: i18n.t("When should I seek further care?"),
        a: (
          <Trans>
            If you are experiencing any of these COVID-19 symptoms, we recommend seeking immediate
            medical attention:
            <br />
            <br />
            <ul>
              <li>
                <b>Difficulty breathing.</b> If you have difficulty breathing after routine
                activities like walking a short distance or you can't recover quickly after climbing
                up a flight of stairs, seek a medical evaluation.
              </li>
              <br />
              <li>
                <b>Chest pain.</b> People with COVID-19 can experience substernal chest pain or
                aching under their breastbone. Any chest pain should be evaluated so a provider can
                determine the specific source of pain or discomfort.
              </li>
              <br />
              <li>
                <b>Changes in mental status.</b> Marked confusion, disorientation, dizziness,
                extreme sleepiness and inability to waken are emergency symptoms that can occur with
                reduced oxygen circulations in the body. If you are experiencing these symptoms, you
                should seek further medical care.
              </li>
              <br />
              <li>
                <b>Blue face or lips.</b> Face or lips turning blue are a sign of hypoxia or a lack
                of adequate oxygen.
              </li>
              <br />
              <li>
                <b>Elevated fever.</b> If your fever lasts for more than 3 days, your temperature
                rises above 103°F, or you experience chest pain or difficulty breathing with your
                fever, contact a doctor for further evaluation.
              </li>
            </ul>
          </Trans>
        ),
      },
      "I just tested positive for COVID-19. What do I do next?": {
        q: i18n.t("I just tested positive for COVID-19. What do I do next?"),
        a: (
          <Trans>
            <ul>
              <li>
                Stay home. Most people with COVID-19 have mild illness and can recover at home
                without medical care. Do not leave your home, except to get medical care, and try to
                stay isolated from others in your home. Do not visit public areas.
              </li>
              <br />
              <li>
                Take care of yourself. Get rest, stay hydrated, and monitor your symptoms. Take
                over-the-counter medicines, such as acetaminophen, to help you feel better.
              </li>
              <br />
              <li>
                Stay in touch with your doctor. Call before you get medical care. Be sure to get
                care if you have trouble breathing, or have any other emergency warning signs.
              </li>
              <br />
              <li>Avoid public transportation, ride-sharing, or taxis.</li>
              <br />
              <li>
                Let the people you have been in contact with know that you have tested positive.
              </li>
              <br />
            </ul>
          </Trans>
        ),
      },
      "I have COVID symptoms, but my test came back negative. Does that mean I'm safe?": {
        q: i18n.t(
          "I have COVID symptoms, but my test came back negative. Does that mean I'm safe?",
        ),
        a: (
          <Trans>
            A negative antigen test result is not as definite as a positive. There is a higher
            chance of false negatives with antigen tests. If you have a negative result on an
            antigen test, your doctor may order a PCR test to confirm the result.
            <br />
            <br />
            Results may also be affected by the timing of the test. For example, if you are tested
            on the day you were infected, your test result is almost guaranteed to come back
            negative, because there are not yet enough viral particles in your nose or saliva to
            detect. The chance of getting a false negative test result decreases if you are tested a
            few days after you were infected, or a few days after you develop symptoms.
          </Trans>
        ),
      },
      "What's the difference between isolation and quarantine?": {
        q: i18n.t("What's the difference between isolation and quarantine?"),
        a: (
          <Trans>
            <b>Isolation</b> is what you do if you have COVID-19 symptoms, or have tested positive.
            Isolation means you stay home and away from others (including household members) for the
            recommended period of time to avoid spreading illness.
            <br />
            <br />
            <b>Quarantine</b> is what you do if you have been exposed to COVID-19. Quarantine means
            you stay home and away from others for the recommended period of time in case you are
            infected and are contagious. Quarantine becomes isolation if you later test positive for
            COVID-19 or develop symptoms.
          </Trans>
        ),
      },
      "How long should I isolate myself?": {
        q: i18n.t("How long should I isolate myself?"),
        a: (
          <Trans>
            <ol>
              <li>
                If you have confirmed or suspected COVID-19 and have symptoms, you can stop your
                home isolation when:
                <ul>
                  <br />
                  <li>
                    You’ve been fever-free for at least 24 hours without the use of fever-reducing
                    medication AND{" "}
                  </li>
                  <br />
                  <li>Your symptoms have gotten better, AND </li>
                  <br />
                  <li>At least 10 days have gone by since your symptoms first appeared. </li>
                  <br />
                </ul>
              </li>
              <br />
              <li>
                If you tested positive for COVID-19, but have not had any symptoms, you can stop
                your home isolation when:
                <ul>
                  <br />
                  <li>
                    At least 10 days have gone by since the date of your first positive COVID-19
                    test, AND
                  </li>
                  <br />
                  <li>You have not gotten sick with COVID-19</li>
                </ul>
                <br />
              </li>
            </ol>
          </Trans>
        ),
      },
      "Do I need to tell my doctor before I go in for evaluation?": {
        q: i18n.t("Do I need to tell my doctor before I go in for evaluation?"),
        a: i18n.t(
          "Before you go to the doctor, call and tell your health care provider you have COVID-19, or are being evaluated for COVID-19. Put on a face covering before you enter the building. These steps will help keep people in the office or waiting room from getting sick.",
        ),
      },
      "Are there any medications I should avoid taking if I have COVID-19?": {
        q: i18n.t("Are there any medications I should avoid taking if I have COVID-19?"),
        a: i18n.t(
          "Currently, there is no evidence to suggest that taking any specific medications, like blood pressure medication or ibuprofen, leads to more severe illness from COVID-19. Continue to take medications and to follow the treatment plan as prescribed by your healthcare provider. Any changes to your medications should only be made after talking with your healthcare provider.",
        ),
      },
      "How long does it take for antibodies to show up after being infected with the coronavirus disease?":
        {
          q: i18n.t(
            "How long does it take for antibodies to show up after being infected with the coronavirus disease?",
          ),
          a: i18n.t(
            "An antibody test may not show if you have a current COVID-19 infection because it can take 1–3 weeks after infection for your body to make antibodies. To see if you are currently infected, you need a viral test.",
          ),
        },
      "I tested positive for antibodies. Does that mean I'm immune?": {
        q: i18n.t("I tested positive for antibodies. Does that mean I'm immune?"),
        a: i18n.t(
          "A positive antibody test does not mean you are immune from COVID-19 as it is not known whether having antibodies will protect you from getting infected again. It also does not indicate whether you can infect other people.",
        ),
      },
      "How do I know I have fully recovered from COVID? When is it safe to be around people?": {
        q: i18n.t(
          "How do I know I have fully recovered from COVID? When is it safe to be around people?",
        ),
        a: (
          <Trans>
            You can be around others after: <br />
            <br />
            <ul>
              <li>
                You’ve been fever-free for at least 24 hours without the use of fever-reducing
                medication AND{" "}
              </li>
              <br />
              <li>Your symptoms have gotten better, AND </li>
              <br />
              <li>At least 10 days have gone by since your symptoms first appeared.</li>
              <br />
            </ul>
            Most people do not require recurring testing however if your healthcare provider
            recommends testing, they will let you know when you can resume being around others based
            on your test results.
          </Trans>
        ),
      },
      "What can I expect during my COVID-19 testing appointment?": {
        q: i18n.t("What can I expect during my COVID-19 testing appointment?"),
        a: (
          <Trans>
            {i18n.t(
              "After booking your appointment online, go to the location during your scheduled time. Once inside, a provider will take your vitals and the test samples. A doctor will evaluate you either onsite or virtually and be able to answer any questions you have about COVID-19.",
            )}
            <br />
            <br />
            {i18n.t(
              "All patients visiting our COVID-19 testing locations will receive diagnostic tests on-site. You can also choose to have an antibody test during your appointment.",
            )}
            <br />
            <br />
            {i18n.t(
              "Diagnostic tests are used by doctors to diagnose people with or without symptoms related to COVID-19. Specimen samples are taken from the patient's nose or throat, with results in 5-7 days.",
            )}
            <br />
            <br />
            {i18n.t(
              "Antibody tests check your blood by looking for antibodies, which may tell you if you had a past infection with the virus that causes COVID-19. You will get your results in 2-3 days.",
            )}
          </Trans>
        ),
      },
      "Will a COVID-19 test and evaluation affect whether I’m eligible for a free wellness visit through my insurance?":
        {
          q: i18n.t(
            "Will a COVID-19 test and evaluation affect whether I’m eligible for a free wellness visit through my insurance?",
          ),
          a: i18n.t(
            "These are separate benefits through your insurance and should have no impact. Contact your insurance company before going in for your evaluation if you have questions or concerns about billing.",
          ),
        },
      "Can I get a COVID-19 vaccination or COVID-19 booster at the same time as a flu vaccine?": {
        q: i18n.t(
          "Can I get a COVID-19 vaccination or COVID-19 booster at the same time as a flu vaccine?",
        ),
        a: i18n.t("Yes, you can get both vaccinations at the same time."),
      },
      "Is my employee's health information secure?": {
        q: i18n.t("Is my employee’s health information secure?"),
        a: (
          <Trans>
            Carbon Health follows strict HIPAA compliance regulations to ensure the safety and
            protection of employee health data. This is Carbon Health's privacy policy (
            <a
              href="https://carbonhealth.com/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="tdu"
            >
              https://carbonhealth.com/privacy-policy
            </a>
            ). Employees' COVID-19 vaccination status will be shared with employers, and employees
            will be clearly notified prior to that data being shared.
          </Trans>
        ),
      },
      "What type of data does the dashboard show?": {
        q: i18n.t("What type of data does the dashboard show?"),
        a: i18n.t(
          "Our Covid tracking dashboard displays all doses of a Covid-19 vaccination, dates of vaccinations and brand(s) administered. We also display Covid test results and symptom tracking for members.",
        ),
      },
      "Is the dashboard updated with real time data?": {
        q: i18n.t("Is the dashboard updated with real time data?"),
        a: i18n.t(
          "Yes, the dashboard will show the latest information on your employees' COVID-19 vaccination and testing status. It will also notate those who opted out for medical or religious reasons. Depending on your company’s policy, your organization can choose if employees who opt out need to do recurrent testing. Testing and vaccinations done through Carbon Health will also reflect in the dashboard.",
        ),
      },
      "Can employees upload vaccine information if they did not get their vaccination through Carbon Health?":
        {
          q: i18n.t(
            "Can employees upload vaccine information if they did not get their vaccination through Carbon Health?",
          ),
          a: i18n.t(
            "Yes, you can now upload your CDC compliant vaccination card to Health Pass using the photo-upload feature in the app.",
          ),
        },
      "Can an employee opt-out for religious or medical reasons?": {
        q: i18n.t("Can an employee opt-out for religious or medical reasons?"),
        a: i18n.t(
          "Yes, the dashboard can be built to follow your own company's vaccination status policy, thereby showing exemptions for those who choose to opt-out for religious or medical reasons.",
        ),
      },
      "How can I update my digital Health Pass?": {
        q: i18n.t("How can I update my digital Health Pass?"),
        a: (
          <Trans>
            For missing vaccination doses that were administered by a Carbon-operated site before
            September 25th, 2021, a request may be submitted through in-app messaging or by emailing{" "}
            <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a>. All other
            inquiries should be directed to{" "}
            <a href="https://chat.myturn.ca.gov/">https://chat.myturn.ca.gov/</a>.
            <br />
            <br />
            OR
            <br /> <br /> Patients who are missing a vaccination dose from their digital Health Pass
            record that was administered by a Carbon-operated site before September 25th, 2021 can
            submit a request through in-app messaging or by emailing{" "}
            <a href="mailto:support@carbonhealth.com">support@carbonhealth.com</a>. All other
            inquiries should be directed to{" "}
            <a href="https://chat.myturn.ca.gov/">https://chat.myturn.ca.gov/</a>.
          </Trans>
        ),
      },
      "How can I update my CAIR record?": {
        q: i18n.t("How can I update my CAIR record?"),
        a: (
          <Trans>
            If your California State vaccination record is missing or incomplete, CAIR has
            recommended you take the following actions. <br />
            1) Complete this web form:{" "}
            <a href="https://chat.myturn.ca.gov/?id=17">https://chat.myturn.ca.gov/?id=17</a> <br />
            2) Call 833-422-4255 ext 1
          </Trans>
        ),
      },
      "Where can I find a copy of my official vaccination records in CAIR?": {
        q: i18n.t("Where can I find a copy of my official vaccination records in CAIR?"),
        a: (
          <Trans>
            A copy of your vaccination record may be requested by completing the following web form:{" "}
            <a href="https://cairforms.cairweb.org/AuthorizationToRelease/AuthorizationToRelease">
              https://cairforms.cairweb.org/AuthorizationToRelease/AuthorizationToRelease
            </a>
            <br />
            <br />
            If you were vaccinated at a Carbon Health site, you can also request a copy by emailing{" "}
            <a href="mailto:vax-replacement-record@carbonhealth.com" className="wsnw">
              vax-replacement-record@carbonhealth.com
            </a>
            .
          </Trans>
        ),
      },
      "What type of test results are required prior to travel between the US and Canada?": {
        q: i18n.t(
          "What type of test results are required prior to travel between the US and Canada?",
        ),
        a: (
          <Trans>
            Canada requires all passengers flying to and from the US have a PCR or NAAT test within
            72 hours of their flight, regardless of vaccination status. Antigen testing is not
            accepted. When you arrive at the border, you must present an accepted negative molecular
            test result (paper or electronic proof) that includes:
            <ul className="disc">
              <li>Traveller name and date of birth</li>
              <li>
                Name and civic address of the laboratory/location/facility that administered the
                test
              </li>
              <li>The date on which the test was conducted</li>
              <li>The type of test conducted</li>
              <li>The test result</li>
            </ul>
          </Trans>
        ),
      },
      "What type of documentation is required prior to boarding an Air Canada flight between the US and Canada?":
        {
          q: i18n.t(
            "What type of documentation is required prior to boarding an Air Canada flight between the US and Canada?",
          ),
          a: i18n.t(
            "Before arriving at the airport, you may upload your COVID-19 health document during online check in, or through Manage My Booking. Alternatively, you can simply bring the original copy (physical or digital) of your COVID-19 health document with you to the airport so that it can be validated by one of the airport agents. ",
          ),
        },
      "How do I verify proof of vaccination before re-entering Canada?": {
        q: i18n.t("How do I verify proof of vaccination before re-entering Canada?"),
        a: i18n.t(
          "If you are fully vaccinated, Canada requires that you use ArriveCAN to upload your vaccination record prior to re-entry. You will also need to bring a physical copy of your vaccine record.",
        ),
      },
      "What should I do if I'm having difficulty uploading a copy of my vaccination record to ArriveCAN?":
        {
          q: i18n.t(
            "What should I do if I'm having difficulty uploading a copy of my vaccination record to ArriveCAN?",
          ),
          a: (
            <Trans>
              Carbon Health is not partnered with ArriveCAN and therefore unable to troubleshoot any
              technical issues. ArriveCAN has recommended that you call their support line at
              1-833-283-7403 (toll-free from Canada or the U.S) or reach out via their online form:
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="tdu ml1"
                href="https://www.canada.ca/en/public-health/services/diseases/coronavirus-disease-covid-19/arrivecan/contact-us.html"
              >
                ArriveCAN Contact Form
              </a>
              .
            </Trans>
          ),
        },
      "How to sign up and access test results": {
        q: i18n.t("How to sign up and access test results"),
        a: (
          <Trans>
            <div className="bold">US mobile devices and Canadian iOS devices:</div>
            <ul className="disc">
              <li>Download Carbon Health app</li>
              <li>Sign in or create a new one account</li>
              <li>Book a travel clearance appointment</li>
              <li>
                If you have notifications turned on, you will receive an SMS text when your results
                have been published
              </li>
              <li>
                Print results directly from your mobile device or download the PDF file directly
              </li>
              <li>Upload file to Air Canada account during online check in</li>
              <li>
                Proof of negative test will also be accepted in printed or original electronic form
              </li>
            </ul>

            <div className="bold mt6">Canadian Android users ONLY:</div>
            <ul className="disc">
              <li>Book an appointment</li>
              <li>
                During the scheduling process, you will be prompted to sign in or create a Carbon
                Health account.
              </li>
              <li>
                Log in to account on desktop or mobile web app at{" "}
                <a href="https://carbonhealth.com/patients/" className="tdu">
                  https://carbonhealth.com/patients/
                </a>
              </li>
              <li>
                After your results have been published, you will be able to print directly from
                mobile device or download a PDF via email or SMS
              </li>
              <li>Upload file to Air Canada account during online check in</li>
              <li>
                Alternatively, download file to desktop and upload to Air Canada account (less
                complicated)
              </li>
              <li>Proof of test will also be accepted in printed or original electronic form.</li>
            </ul>
          </Trans>
        ),
      },
      "What type of testing method is required for Japan?": {
        q: i18n.t("What type of testing method is required for Japan?"),
        a: i18n.t("Japan only is requesting the Nasopharyngeal testing method."),
      },
      "Why is Carbon Health out-of-network with my plan?": {
        q: i18n.t("Why is Carbon Health out-of-network with my plan?"),
        a: i18n.t(
          "Out-of-network service and benefit coverage can vary based on contract status, geographic location, or the type of care visit (urgent or primary). While we are contracted with most insurance carriers, PPOs, and Medicare,  we always recommend giving your health insurance plan provider a call to confirm your coverage and benefits. The number can be found on your plan card.",
        ),
      },
      "My plan has coverage for out-of-network services. Why do I have to pay up-front?": {
        q: i18n.t(
          "My plan has coverage for out-of-network services. Why do I have to pay up-front?",
        ),
        a: (
          <Trans>
            <p>
              We always suggest checking with your insurance company about your out-of-network
              coverage and benefits before seeking care. Our policy is to estimate your payment by
              charging our flat self-pay rate for your treatment prior to the visit.
            </p>

            <p className="mt-8">
              You can check out our flat self-pay rates at
              <Link href={v5Pages.insurance} className="underline ml-1">
                https://carbonhealth.com/insurance-pricing
              </Link>{" "}
              or inquire at the front desk before we request payment for your visit. Many insurance
              companies will still reimburse for out-of-network care at Carbon Health. As a
              courtesy, we will submit your insurance claim and refund you the balance depending on
              your benefit.
            </p>
          </Trans>
        ),
      },
      "I have questions about my bill. Who can I talk to?": {
        q: i18n.t("I have questions about my bill. Who can I talk to?"),
        a: (
          <Trans>
            If you have any questions about your bill or claim, please contact us at
            <a href={`${supportLink}/contact/contact-us`} className="underline ml-1">
              {supportLink}/contact/contact-us
            </a>
            .
          </Trans>
        ),
      },
      "How does Carbon Health come up with their self-pay prices?": {
        q: i18n.t("How does Carbon Health come up with their self-pay prices?"),
        a: i18n.t(
          "We look at comparable services in a market, healthcare costs, and also factor in inflation. Our goal is to remain affordable and accessible to the average patient, while ensuring our on-site teams are fairly compensated for the services they provide.",
        ),
      },
      "Why do Carbon Health's self-pay prices vary by state?": {
        q: i18n.t("Why do Carbon Health's self-pay prices vary by state?"),
        a: (
          <Trans>
            Like most healthcare providers, our ability to offer accessible costs to patients often
            depends on geography. Some factors that affect pricing across states or regions include
            labor costs, staffing costs, cost of living differences, and other healthcare market
            dynamics. Our team strives to be clear and straightforward with you about our self-pay
            fees, and you can always look them up here:
            <Link href={v5Pages.insurance} className="underline ml-1">
              https://carbonhealth.com/insurance-pricing
            </Link>
            .
          </Trans>
        ),
      },
      "How does my Student Administrative Health Fee (SAHF) work with my insurance?": {
        q: i18n.t("How does my Student Administrative Health Fee (SAHF) work with my insurance?"),
        a: (
          <Trans>
            <p>
              <strong>If you are covered by commercial insurance (Aetna, Cigna, BCBS etc.)</strong>,
              your college’s SAHF will pay up to $35 per visit of the remaining patient
              responsibility (i.e., copay, coinsurance, and/or deductible) not paid by your
              insurance. You will be billed for any remaining patient responsibility from your
              visit.
            </p>
            <p className="mt-4">
              <strong>If you are covered by MassHealth or Tricare</strong> insurance will be billed
              after the $35 per visit benefit is provided by your college’s SAHF for eligible
              medical expenses specified in the plan document. Carbon Student Health does not
              participate with Medicare at the Colleges of the Fenway locations.
            </p>
            <p className="mt-4">
              The colleges reserve the right to use SAHF and/or other available funds to cover
              charges incurred by a student at Carbon Health Student Health Services, and not bill
              the student’s insurance, when the SAHF Administrator or their delegate determines that
              it is in the best interests of either the student or the college. Considerations for
              such a determination may include, but are not limited to, highly sensitive care
              situations where a student’s well being could be in jeopardy if charges were submitted
              to the student’s personal health insurance and/or the student was required to pay for
              the service or supply. Such best-interest determinations will be made solely by the
              Plan Administrator or their delegate on a case-by-case basis.
            </p>
            <p className="mt-4">
              * The funding from your college to support providing student health and wellness
              programs has been designated by federal regulation to be a Student Administrative
              Health Fee (SAHF), regardless of whether there is a designated health fee in tuition
              and/fees or funding is derived from an allocation from the college.
            </p>
          </Trans>
        ),
      },
      "Will this student benefit be available at other Carbon Health locations?": {
        q: i18n.t("Will this student benefit be available at other Carbon Health locations?"),
        a: (
          <Trans>
            All terms and conditions in the Student Administrative Health Fee (SAHF) plan documents
            apply to billing incurred by students at the Treehouse location. No SAHF benefits are
            applicable to medical expenses incurred at other medical care locations.
          </Trans>
        ),
      },
      "Do I have to change my primary care provider to use the Carbon Health Student Services location?":
        {
          q: i18n.t(
            "Do I have to change my primary care provider to use the Carbon Health Student Services location?",
          ),
          a: (
            <Trans>
              Services will be available to many students without having to change the primary care
              provider designated with your insurance/health plan as Carbon Health bills as an
              urgent care location.
            </Trans>
          ),
        },
      "When or how will I know if there is any patient responsibility due?": {
        q: i18n.t("When or how will I know if there is any patient responsibility due?"),
        a: (
          <Trans>
            While we will bill your insurance promptly, due to insurance processing times, you may
            experience a delay in receiving a bill. This means you may receive your bill several
            weeks to several months after your visit. If you have any questions about your bill,
            please contact Carbon Health Support at{" "}
            <a href="tel:415-200-2133" className="underline">
              415-200-2133
            </a>{" "}
            or visit our website (
            <a href="https://support.carbonhealth.com" className="underline">
              support.carbonhealth.com
            </a>
            ).
          </Trans>
        ),
      },
      "Is Carbon Health at the Colleges of the Fenway location in network with my health insurance?":
        {
          q: i18n.t(
            "Is Carbon Health at the Colleges of the Fenway location in network with my health insurance?",
          ),
          a: (
            <Trans>
              <p>
                Carbon Health is an in-network participating provider with major health insurers in
                Massachusetts including:
              </p>
              <ul className="disc">
                <li>Aetna</li>
                <li>Blue Cross and Blue Shield</li>
                <li>Cigna</li>
                <li>Harvard Pilgrim</li>
                <li>MassHealth</li>
                <li>MultiPlan</li>
                <li>United Healthcare</li>
                <li>Tufts Health Plan</li>
              </ul>
              <p className="mt-6">
                If you don&apos;t see your insurance carrier, please give your health insurance plan
                provider a call to double check. The number can be found on your plan card. Service
                and benefit coverage vary based on geographic location.
              </p>
            </Trans>
          ),
        },
      "If I'm not ready to book an appointment, can I register now?": {
        q: i18n.t("If I’m not ready to book an appointment, can I register now?"),
        a: (
          <Trans>
            Yes! On this webpage, click “get started” and then close the pop up window that asks
            ‘What brings you in?’ by selecting “quit”. Click on ‘log in’ at the top right corner.
            Click “sign up”. Follow the prompts to create an account. Remember to come back to this
            unique link if you want to schedule an appointment at the Treehouse location.
          </Trans>
        ),
      },
    }),
    [i18n],
  );
  // FOR UTI, check "Do you accept insurance?"
  // For st marys how to book an appointment, need st marys schedulinglink
  return useDeepCompareMemo(
    () =>
      items
        ? items.compact().map(item => (isSectionDivider(item) ? item : faqIndex[item]))
        : faqIndex.vals(),
    [items],
  );
};
